import { loadScript, getConsentMap } from "../../generalUtils";
import { getRegion, isAPAC, isQuebec } from "../../generalUtils/envFns";
import { logger, AnalyticsModuleBootstrapError } from "../../debug";
import type { QuantumMetricPluginConfig } from "./types";
import { setQuantumMetricConsentState, trackQuantumMetricEvent } from "./utils";

export default (config: QuantumMetricPluginConfig) => {
  let quantumScriptLoading: ReturnType<typeof loadScript> | undefined;
  const { enabled, debug, loadNetworkInterceptor, scriptUrl } = config;

  // Quantum Metric script URL - use config.scriptUrl if provided, otherwise default to NA
  const finalScriptUrl =
    scriptUrl || "https://cdn.quantummetric.com/qscripts/quantum-lululemon.js";

  // Consent tracking
  let hasConsent = false;

  const updateConsent = (consented: boolean = false) => {
    hasConsent = consented;
    setQuantumMetricConsentState(consented);

    if (debug) {
      logger.debug("Quantum Metric consent updated", { consented });
    }
  };

  /**
   * Determine if user has granted consent based on region
   */
  const getInitialConsent = (): boolean => {
    const region = getRegion();

    // APAC regions: opt-out model (capture by default)
    if (isAPAC()) {
      logger.debug(
        "APAC region detected — Quantum Metric capture enabled by default"
      );
      return true;
    }

    // Opt-in regions: require consent
    const { C0002 } = getConsentMap();

    // US and non-Quebec CA: respect performance consent (C0002) — matches Adobe's classification
    if (region === "us" || (region === "ca" && !isQuebec())) {
      const consented =
        !navigator?.["globalPrivacyControl"] &&
        (typeof C0002 === "undefined" || Number(C0002) === 1);
      return consented;
    }

    // EMEA and other regions: default deny until consent granted
    return Number(C0002) === 1;
  };

  /**
   * Determine consent based on region for consent updates
   */
  const getUpdatedConsent = (performance: unknown): boolean => {
    // APAC regions use opt-out model — always consented regardless of C0002
    if (isAPAC()) {
      return true;
    }
    // All other regions use C0002 (performance) — matches Adobe's classification of QM
    return Number(performance) === 1;
  };

  const pushQMEventDataLayer = (event: string) => {
    // push it to whichever dataLayer
    window.eswDataLayer?.push({ event });
    window.dataLayer?.push({ event });
    // window.automatedDataLayer?.push({ event });
  };

  return {
    name: "quantum-metric-plugin",
    config,
    enabled,
    debug,
    loaded() {
      if (quantumScriptLoading !== undefined) {
        return quantumScriptLoading.isReady();
      }
      // Check if API was already loaded
      return (
        typeof window !== "undefined" && window.QuantumMetricAPI !== undefined
      );
    },
    ready() {
      logger.debug("Quantum Metric plugin is ready");
    },
    track: ({ payload }) => {
      if (debug === true) {
        const { event, properties } = payload;

        try {
          // Only track automated data events for parity validation
          if (
            event === "automatedData" &&
            properties?.payload?.event?.eventName
          ) {
            const eventName = properties.payload.event.eventName;

            // If track is called then the plugin is enabled and consent has been granted, so we can call the trackQuantumMetricEvent function
            // Check the pluginInitialize function and consentLevel function call
            // `abort` will prevent the plugin from being loaded at all

            trackQuantumMetricEvent(eventName, {
              eventName,
              timestamp: Date.now(),
            });

            if (debug) {
              logger.debug("Quantum Metric event tracked", { eventName });
            }
          }
        } catch (error) {
          logger.warn(
            new AnalyticsModuleBootstrapError(
              "Quantum Metric plugin track error",
              { cause: error }
            )
          );
        }
      }
    },
    initialize() {
      logger.debug("Quantum Metric plugin initializing");

      if (typeof window === "undefined") {
        logger.warn(
          "Quantum Metric plugin cannot initialize outside of browser context"
        );
        // Throwing an error here will prevent the plugin from being loaded, which is the desired behavior for non-browser contexts.
        throw new AnalyticsModuleBootstrapError(
          "Quantum Metric plugin cannot initialize outside of browser context"
        );
      }

      const initialConsent = getInitialConsent();
      const existingScript = document.querySelector(
        `script[src="${finalScriptUrl}"]`
      );
      if (existingScript) {
        if (window.QuantumMetricAPI) {
          logger.debug("Quantum Metric API already loaded");
          updateConsent(initialConsent);
          pushQMEventDataLayer("quantumMetric_loaded");
        } else {
          existingScript.addEventListener("load", () => {
            logger.debug("Quantum Metric script loaded successfully");

            updateConsent(initialConsent);

            pushQMEventDataLayer("quantumMetric_loaded");
          });
          existingScript.addEventListener("error", (error) => {
            logger.warn(
              new AnalyticsModuleBootstrapError(
                "Failed to load Quantum Metric script",
                { cause: error }
              )
            );
          });
        }
      } else {
        // Load the script asynchronously
        quantumScriptLoading = loadScript(finalScriptUrl);
        quantumScriptLoading.promise.then(() => {
          logger.debug("Quantum Metric script loaded successfully");
          updateConsent(initialConsent);
          pushQMEventDataLayer("quantumMetric_loaded");
        });

        quantumScriptLoading.promise.catch((error) => {
          logger.warn(
            new AnalyticsModuleBootstrapError(
              "Failed to load Quantum Metric script",
              { cause: error }
            )
          );
        });
      }
    },
    consentUpdate({ payload }) {
      const { C0002 } = payload;
      const consented = getUpdatedConsent(C0002);
      updateConsent(consented);

      if (debug) {
        logger.debug("Quantum Metric consent updated via consentUpdate", {
          C0002,
          consented,
        });
      }
    },
  };
};
